import { Dices } from 'lucide-react'
import { useSessionBuilder } from '../../contexts/SessionBuilderContext'
import { Button, Chip, Textarea, Toggle } from '../ui'

// Quick vibe shortcuts that inform the free-text prompt (design-system §6.4).
const VIBE_CHIPS = ['Chill', 'Epic', 'Quick', 'Silly', 'Co-op grind', 'Mind-bender', 'Cozy', 'Competitive']

interface VibeStepProps {
  pending: boolean
  onBack: () => void
  onRoll: () => void
}

export function VibeStep({ pending, onBack, onRoll }: VibeStepProps) {
  const { state, dispatch } = useSessionBuilder()

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h2 className="font-display text-3xl leading-tight text-text-strong">What&apos;s the vibe tonight?</h2>
        <p className="text-sm text-text-muted">
          A mood, not a filter — it tempers the picks, never lowers the bar.
        </p>
      </header>

      <section className="space-y-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Quick vibe">
          {VIBE_CHIPS.map((chip) => (
            <Chip
              key={chip}
              selected={state.vibeChips.includes(chip)}
              onToggle={() => dispatch({ type: 'toggleVibeChip', chip })}
            >
              {chip}
            </Chip>
          ))}
        </div>

        <Textarea
          label="In your words"
          rows={3}
          value={state.vibeText}
          onChange={(e) => dispatch({ type: 'setVibeText', text: e.target.value })}
          placeholder="…something chill, not dark and gritty"
        />
        {state.vibeChips.length > 0 && (
          // muted, not faint: the folded-in chip list is real content (WCAG 1.4.3).
          <p className="text-xs text-text-muted">
            We&apos;ll fold in: {state.vibeChips.join(', ')}
          </p>
        )}
      </section>

      {/* D-layer opt-in: reveal held `uncertain` picks as wildcards. */}
      <section className="rounded-card bg-surface p-4 ring-1 ring-border">
        <Toggle
          label="Open to a wildcard"
          description="Let an off-profile gem or a niche indie sneak in. A miss won't punish your picks."
          checked={state.wildcardOptIn}
          onChange={(checked) => dispatch({ type: 'setWildcardOptIn', optIn: checked })}
        />
      </section>

      {/* flex-wrap: same graceful-stack rule as the roster footer. */}
      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button type="button" variant="secondary" onClick={onBack} disabled={pending}>
          Back
        </Button>
        <Button type="button" onClick={onRoll} loading={pending}>
          {!pending && <Dices className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />}
          {pending ? 'Rolling…' : 'Roll for it'}
        </Button>
      </footer>
    </div>
  )
}
